import { useNavigate } from 'react-router-dom';
import { Check, Car, Cpu, Bell } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { SkeletonRows } from '@/components/ui/Skeleton';
import { ErrorState } from '@/components/states/ErrorState';
import { useDevices, useVehicles } from '@/features/shared/queries';
import { AddVehicleForm } from '@/features/vehicles/AddVehicleForm';
import { ClaimDeviceForm } from './ClaimDeviceForm';
import { NotificationOptIn } from '@/features/settings/NotificationOptIn';
import { cn } from '@/lib/cn';

interface StepProps {
  index: number;
  title: string;
  description: string;
  icon: LucideIcon;
  done: boolean;
  children: React.ReactNode;
}

function Step({ index, title, description, icon: Icon, done, children }: StepProps) {
  return (
    <Card>
      <CardHeader
        title={`${index}. ${title}`}
        description={description}
        action={
          done ? (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-ok/20 bg-ok-soft px-2 py-0.5 text-[12px] font-medium text-ok">
              <Check className="size-3" aria-hidden />
              Done
            </span>
          ) : (
            <Icon className="size-4 text-ink-muted" aria-hidden />
          )
        }
      />
      <CardBody className={cn(done && 'opacity-60')}>{children}</CardBody>
    </Card>
  );
}

/**
 * Getting a new account to the point where it can actually detect a crash: a
 * vehicle to attach to, a device linked to it, and somewhere to send the alert.
 * Every step is skippable and the page is resumable — the overview's empty states
 * link back here — so nobody gets trapped in a wizard.
 */
export function SetupPage() {
  const navigate = useNavigate();
  const vehicles = useVehicles();
  const devices = useDevices();

  if (vehicles.isPending || devices.isPending) {
    return (
      <>
        <PageHeader title="Set up AutoAware" />
        <Card>
          <SkeletonRows rows={5} />
        </Card>
      </>
    );
  }

  if (vehicles.isError) return <ErrorState error={vehicles.error} onRetry={vehicles.refetch} />;
  if (devices.isError) return <ErrorState error={devices.error} onRetry={devices.refetch} />;

  const vehicleList = vehicles.data ?? [];
  const deviceList = devices.data ?? [];
  const hasVehicle = vehicleList.length > 0;
  const hasDevice = deviceList.length > 0;
  const ready = hasVehicle && hasDevice;

  return (
    <>
      <PageHeader
        title="Set up AutoAware"
        description="Three steps to working crash detection. You can leave and come back at any point."
        action={
          <Button variant={ready ? 'primary' : 'ghost'} onClick={() => navigate('/overview')}>
            {ready ? 'Go to overview' : 'Skip for now'}
          </Button>
        }
      />

      <div className="max-w-2xl space-y-4">
        <Step
          index={1}
          title="Add your vehicle"
          description="The vehicle your device will be fitted to."
          icon={Car}
          done={hasVehicle}
        >
          {hasVehicle ? (
            <ul className="space-y-1 text-[13px] text-ink-secondary">
              {vehicleList.map((vehicle) => (
                <li key={vehicle.id}>
                  <span className="font-medium text-ink">{vehicle.license_plate}</span> ·{' '}
                  {vehicle.vehicle_type}
                </li>
              ))}
            </ul>
          ) : (
            <AddVehicleForm />
          )}
        </Step>

        <Step
          index={2}
          title="Link your device"
          description="Use the device ID and claim code printed on the card in the box."
          icon={Cpu}
          done={hasDevice}
        >
          {hasDevice ? (
            <ul className="space-y-1 text-[13px] text-ink-secondary">
              {deviceList.map((device) => (
                <li key={device.id}>
                  <span className="font-medium text-ink">{device.serial_number}</span> · linked
                </li>
              ))}
            </ul>
          ) : (
            <ClaimDeviceForm vehicles={vehicleList} />
          )}
        </Step>

        <Step
          index={3}
          title="Turn on alerts in this browser"
          description="Optional. Emergency SMS and calls are sent regardless."
          icon={Bell}
          done={false}
        >
          <NotificationOptIn />
        </Step>
      </div>
    </>
  );
}
